<template>
  <v-menu content-class="white" z-index="204" offset-x right v-bind="$attrs" :close-on-content-click="false">

    <template v-slot:activator="{ on }">
      <slot :on="on"></slot>
    </template>

    <v-list style="height: 90vh; height: 90svh;" dense>

      <error-dialog :status="uploadStatus" />

      <sign-in-dialog ref="sign-in" v-model="showSignIn">
        <template v-slot:subtitle>Please sign in to upload custom media</template>
      </sign-in-dialog>

      <input v-show="false" ref="upload" type="file" accept="image/*" @click.stop />

      <dialog-scroll-lock width="300" persistent :value="!!tryDelete">
        <v-card>
          <v-card-title>Are you sure?</v-card-title>
          <v-divider />
          <v-card-text class="pt-4 break-word">Are you sure you want to delete this image?</v-card-text>
          <v-divider />
          <v-card-actions>
            <v-spacer/>
            <v-btn text @click="tryDelete = null">No</v-btn>
            <v-btn text color="primary" @click="deleteTexture(tryDelete)">Yes</v-btn>
          </v-card-actions>
        </v-card>
      </dialog-scroll-lock>

      <template v-for="(texture, i) in textures">
        <v-list-item
          v-if="texture"
          class="pa-0"
          v-bind:class="{ 'primary--text': texture.name == 'Add custom…' }"
          :disabled="(deleting == texture.name) || ((texture.name == 'Add custom…') && loadStatus.pending)"
          :input-value="(texture.name == 'Add custom…') ? false : (value == texture.src)"
          @click="() => (texture.name == 'Add custom…') ? promptUpload() : $emit('input', texture.src)"
        >
          <template v-if="texture.name == 'Add custom…'">
            <v-progress-circular v-if="loadStatus.pending" class="mx-3 my-1" size="24" indeterminate />
            <v-icon v-else class="mx-3 my-1" color="primary" v-html="mdiUpload" />
          </template>
          <v-card
            v-else
            class="flex-shrink-0 mx-2"
            style="background-size: cover;"
            outlined width="32" height="32"
            v-bind:style="{
              'background-image': texture.src ? `url('${texture.src}')` : null,
              'background-color': backgroundColor
            }"
          />
          <v-list-item-title class="mx-3 d-flex align-center">
            <div class="flex-grow-1 texture-name">{{stripUUID(texture.name)}}</div>
            <v-btn
              v-if="texture.custom"
              class="ml-2"
              icon color="warning"
              :loading="deleting == texture.name"
              @click.capture.stop="tryDelete = texture"
            >
              <v-icon v-html="mdiDelete" />
            </v-btn>
          </v-list-item-title>
        </v-list-item>
        <v-divider v-else class="my-2" />
      </template>

    </v-list>
  </v-menu>
</template>


<style scoped>
.texture-name::first-letter {
  text-transform: capitalize;
}
</style>


<script>
import SignInDialog from '@/components/SignInDialog.vue';
import ShareBtn from '@/components/ShareBtn.vue';
import TEXTURES from '@/utils/textures.js';
import stripUUID from '@/filters/strip-uuid.js';
import { mdiTexture, mdiCheck, mdiUpload, mdiDelete } from '@mdi/js';

const NONE = { name: 'None', src: null };
const ADD_CUSTOM = { name: 'Add custom…' };
const NAMED_TEXTURES = TEXTURES.map(x => ({ name: x.slice(0, -4).replace('-', ' '), src: '/textures/' + x }))

export default {
  name: 'texture-menu-btn',

  components: { SignInDialog },

  props: [ 'value', 'backgroundColor' ],

  data() {
    return {
      showSignIn: false,
      userLibrary: [],
      tryDelete: null,
      deleting: null,
      loadStatus: this.$track('load'),
      uploadStatus: this.$track('upload')
    };
  },

  computed: {
    textures() {
      const userTextures = [ ...this.userLibrary ];
      userTextures.sort((a, b) => a.name.localeCompare(b.name));
      return [ NONE, null, ADD_CUSTOM, ...userTextures.map(x => ({ name: x.name, src: x.url, custom: true })), null, ...(this.loadStatus.pending ? [] : NAMED_TEXTURES) ];
    }
  },

  created() {
    this.load();
    Object.assign(this, { stripUUID, mdiTexture, mdiCheck, mdiUpload, mdiDelete })
  },

  methods: {
    async load() {
      this.userLibrary = this.isSignedIn ? (await this.$call('list-user-media-library', { type: 'texture' })) : [];
    },

    async promptUpload() {
      if (!this.isSignedIn) {
        await this.$refs['sign-in'].prompt();
        if (!this.isSignedIn)
          return;
      }

      const el = this.$refs.upload;
      el.onchange = e => {
        const file = e.target.files[0];
        if (file)
          this.upload(file);
        e.target.onchange = null;
        e.target.value = null;
      };
      el.click();
    },

    async upload(file) {
      if (file.size / 1024 / 1024 > 15)
        throw new VisibleError('File is too large (15 MB limit)');

      if (!file.type.startsWith('image'))
        throw new VisibleError('File is not an image file');

      const name = file.name.replace(/\.[^/.]+$/, '');
      const url = await this.$call('upload-to-user-media-library', { name, type: 'texture', file });
      this.userLibrary.push({ name, url });
    },

    async deleteTexture(texture) {
      this.tryDelete = null;
      this.deleting = texture.name;
      try {
        await this.$call('delete-from-user-media-library', { name: texture.name, type: 'texture' });
        this.userLibrary = this.userLibrary.filter(x => x.name != texture.name);
      } finally {
        this.deleting = null;
      }
      if (this.value == texture.src)
        this.$emit('input', null);
    }
  },

  watch: {
    globalUserId: {
      immediate: true,
      handler() {
        this.load();
      }
    }
  }
}
</script>
